import React, { useState } from "react";
import * as Mui from "@mui/material";
import {
  CloudDownload,
  Description,
  DownloadForOffline,
  Cancel,
  Close,
  Edit,
  Delete,
  InfoOutlined,
  ErrorOutlineRounded,
} from "@mui/icons-material";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { employee_use, infoStyle, themes } from "services/constants";
import { designType } from "services/constants/designTokens";
import { useFetch } from "services/hooks/useFetch";
import {
  aadharRegex,
  ifscCodePatterns,
  panRegex,
  specialCharacterRestrict,
} from "services/constants/patterns";
import { OPTIONAL_PERSONAL_COLUMNS } from "./optionalPersonalColumns";

const EMPLOYEE_ID_HEADER = "Employee Id";
const IDENTIFIER_WARNINGS_KEY = "__identifierWarnings";
const IMPORT_HEADERS = [
  EMPLOYEE_ID_HEADER,
  ...OPTIONAL_PERSONAL_COLUMNS.map((column) => column.label),
];

const SENSITIVE_TEXT_FIELD_KEYS = new Set([
  "employee_id",
  "aadhar",
  "pan",
  "esi",
  "pf",
  "uan",
  "accountno",
  "emergency_contact_number",
]);

const IDENTIFIER_FIELD_KEYS = new Set([
  "aadhar",
  "pan",
  "esi",
  "pf",
  "uan",
  "accountno",
]);

const COLUMN_KEY_BY_LABEL = Object.fromEntries(
  OPTIONAL_PERSONAL_COLUMNS.map((column) => [column.label, column.key]),
);
COLUMN_KEY_BY_LABEL[EMPLOYEE_ID_HEADER] = "employee_id";

const optionalFormValue = (value) => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed === "" ? null : trimmed;
};

const csvCellHasValue = (value) => {
  if (value == null) return false;
  return String(value).trim() !== "";
};

export const stripInternalFields = ({
  check_status,
  validation_errors,
  [IDENTIFIER_WARNINGS_KEY]: identifierWarnings,
  ...row
}) => row;

const unwrapExcelTextFormula = (value) => {
  const trimmed = value.trim();
  const match = trimmed.match(/^=\s*"([^"]*)"$/);
  return match ? match[1] : trimmed;
};

const parseExponentText = (value) => {
  const sign = value.startsWith("-") ? -1 : 1;
  const digits = value.replace(/^[+-]/, "");
  if (digits.length > 6) return null;
  let exponent = 0;
  for (const digit of digits) {
    exponent = exponent * 10 + digit.charCodeAt(0) - 48;
  }
  return sign * exponent;
};

// Excel may save long identifiers as scientific notation. Expand only when
// every integer digit is present in the text; appended zeroes imply lost detail.
export const expandScientificIntegerString = (value) => {
  const match = value.trim().match(/^([+-]?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/);
  if (!match) return { value, exact: false };

  const [, sign, integerPart, fractionPart = "", exponentText] = match;
  if (sign === "-") return { value, exact: false };

  const exponent = parseExponentText(exponentText);
  if (exponent == null) return { value, exact: false };
  const decimalShift = integerPart.length + exponent;
  if (decimalShift <= 0) return { value, exact: false };

  const digits = `${integerPart}${fractionPart}`;
  if (decimalShift > digits.length) {
    return { value, exact: false };
  }

  const integerDigits = digits.slice(0, decimalShift);
  const fractionalDigits = digits.slice(decimalShift);
  if (fractionalDigits && !/^0+$/.test(fractionalDigits)) {
    return { value, exact: false };
  }

  return { value: integerDigits.replace(/^0+(?=\d)/, ""), exact: true };
};

export const normalizeImportedIdentifierValue = (key, value) => {
  if (value == null) return { value };

  if (typeof value !== "string") {
    return {
      value: String(value),
      warning:
        "Identifier value was parsed as a number before import. Verify it against the source file.",
    };
  }

  const textValue = SENSITIVE_TEXT_FIELD_KEYS.has(key)
    ? unwrapExcelTextFormula(value)
    : value;
  const trimmed = textValue.trim();
  if (!trimmed || !IDENTIFIER_FIELD_KEYS.has(key) || !isExcelScientific(trimmed)) {
    return { value: textValue };
  }

  const expanded = expandScientificIntegerString(trimmed);
  if (expanded.exact) {
    return { value: expanded.value };
  }

  return {
    value: "",
    warning:
      "Scientific notation does not include enough original digits. Verify and re-enter it as text.",
  };
};

const normalizeIdentifierFields = (row) => {
  const warnings = { ...(row[IDENTIFIER_WARNINGS_KEY] ?? {}) };
  const normalizedRow = { ...row };

  [...SENSITIVE_TEXT_FIELD_KEYS].forEach((key) => {
    if (!(key in normalizedRow)) return;
    const normalized = normalizeImportedIdentifierValue(key, normalizedRow[key]);
    normalizedRow[key] = normalized.value;
    if (normalized.warning) warnings[key] = normalized.warning;
  });

  return Object.keys(warnings).length > 0
    ? { ...normalizedRow, [IDENTIFIER_WARNINGS_KEY]: warnings }
    : normalizedRow;
};

const hasUploadedValue = (value) => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return csvCellHasValue(value);
};

export const mergeCheckedEmployeeValues = (uploadRow, checkedValues) => {
  const normalizedCheckedValues = normalizeIdentifierFields(checkedValues);
  const merged = { ...normalizedCheckedValues };

  Object.entries(uploadRow).forEach(([key, value]) => {
    if (["check_status", "validation_errors"].includes(key)) return;
    if (key === IDENTIFIER_WARNINGS_KEY) {
      merged[key] = { ...(merged[key] ?? {}), ...value };
      return;
    }
    if (hasUploadedValue(value)) {
      merged[key] = value;
    }
  });

  return merged;
};

const cellAddress = (rowIndex, columnIndex) =>
  XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });

const cellTextValue = (cell, key) => {
  if (!cell) return "";
  const value = cell.v ?? cell.w ?? "";
  if (SENSITIVE_TEXT_FIELD_KEYS.has(key)) {
    return value == null ? "" : value;
  }
  return value == null ? "" : String(value);
};

export const worksheetToImportRows = (worksheet) => {
  if (!worksheet?.["!ref"]) return [];
  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  const headers = [];
  for (let columnIndex = range.s.c; columnIndex <= range.e.c; columnIndex += 1) {
    const cell = worksheet[cellAddress(range.s.r, columnIndex)];
    headers[columnIndex] = cell?.v == null ? "" : String(cell.v).trim();
  }

  const rows = [];
  for (let rowIndex = range.s.r + 1; rowIndex <= range.e.r; rowIndex += 1) {
    const row = {};
    headers.forEach((label, columnIndex) => {
      if (!label) return;
      row[label] = cellTextValue(
        worksheet[cellAddress(rowIndex, columnIndex)],
        COLUMN_KEY_BY_LABEL[label],
      );
    });
    rows.push(row);
  }
  return rows;
};

const buildPayloadRow = (rowid, values) => {
  const payload = {
    rowid,
    employee_id: optionalFormValue(values.employee_id),
  };
  OPTIONAL_PERSONAL_COLUMNS.forEach((column) => {
    if (column.key.startsWith("emergency_")) return;
    payload[column.key] = optionalFormValue(values[column.key]);
  });
  const contact = {
    contact_name: optionalFormValue(values.emergency_contact_name),
    relation_type: optionalFormValue(values.emergency_relation_type),
    contact_number: optionalFormValue(values.emergency_contact_number),
  };
  payload.emergency_contacts = Object.values(contact).some(Boolean)
    ? [contact]
    : [];
  return payload;
};

// A row with a blank Employee Id is kept (not dropped) so it shows up in the
// table flagged as an issue instead of silently disappearing from the file.
export const buildUpdateRows = (csvRows) =>
  csvRows
    .filter((row) => Object.values(row).some((value) => csvCellHasValue(value)))
    .map((row, index) => {
      const warnings = {};
      const employeeId = normalizeImportedIdentifierValue(
        "employee_id",
        row[EMPLOYEE_ID_HEADER],
      );
      const values = { employee_id: employeeId.value };
      if (employeeId.warning) warnings.employee_id = employeeId.warning;
      OPTIONAL_PERSONAL_COLUMNS.forEach((column) => {
        const normalized = normalizeImportedIdentifierValue(
          column.key,
          row[column.label],
        );
        values[column.key] = normalized.value;
        if (normalized.warning) warnings[column.key] = normalized.warning;
      });
      const payload = buildPayloadRow(index + 1, values);
      return Object.keys(warnings).length > 0
        ? { ...payload, [IDENTIFIER_WARNINGS_KEY]: warnings }
        : payload;
    });

// Excel shows long digit strings as 1.23457E+11 and drops the rest of the digits on save.
const isExcelScientific = (value) => /^\d+(\.\d+)?E[+-]?\d+$/i.test(value);
const EXCEL_SCIENTIFIC_MESSAGE =
  "Excel changed this identifier to scientific notation and may have lost digits. Verify and re-enter it as text.";

// Mirrors the validation on the regular Employee edit "Personal Info" tab
// (EditEmpPersonalInfo.js) so a bulk-imported row isn't held to stricter
// rules than editing the same employee one at a time would require.
const validateEditValues = (values) => {
  const errors = {};
  if (!optionalFormValue(values.employee_id)) {
    errors.employee_id = "Employee ID is required";
  }

  const dob = optionalFormValue(values.dob);
  if (dob && !/^\d{2}-\d{2}-\d{4}$/.test(dob)) {
    errors.dob = "Enter date as dd-mm-yyyy";
  }

  const aadhar = optionalFormValue(values.aadhar);
  if (aadhar && isExcelScientific(aadhar)) {
    errors.aadhar = EXCEL_SCIENTIFIC_MESSAGE;
  } else if (aadhar && !aadharRegex.test(aadhar)) {
    errors.aadhar = "Must be exactly 12 digits";
  }

  const pan = optionalFormValue(values.pan);
  if (pan && !panRegex.test(pan)) {
    errors.pan = "Enter PAN in format AAAAA9999A";
  }

  const uan = optionalFormValue(values.uan);
  if (uan && isExcelScientific(uan)) {
    errors.uan = EXCEL_SCIENTIFIC_MESSAGE;
  }

  ["account_holder_name", "bankname"].forEach((key) => {
    const value = optionalFormValue(values[key]);
    if (!value) return;
    if (value.length < 3) {
      errors[key] = "Minimum 3 characters required";
    } else if (!/[a-zA-Z]/.test(value)) {
      errors[key] = "Must include at least one letter";
    }
  });

  const ifsc = optionalFormValue(values.ifsc);
  if (ifsc && (ifsc.length < 11 || !ifscCodePatterns.test(ifsc))) {
    errors.ifsc = "Enter a valid format: ABCD0123456";
  }

  const contactName = optionalFormValue(values.emergency_contact_name);
  const relationType = optionalFormValue(values.emergency_relation_type);
  const contactNumber = optionalFormValue(values.emergency_contact_number);
  if (contactName || relationType || contactNumber) {
    if (!contactName) errors.emergency_contact_name = "Name is required";
    if (!relationType) errors.emergency_relation_type = "Relation is required";
    if (!contactNumber) {
      errors.emergency_contact_number = "Number is required";
    } else if (!/^\d{10}$/.test(contactNumber)) {
      errors.emergency_contact_number = "Enter a 10-digit number";
    }
  }

  return errors;
};

const fieldLabel = (key) =>
  key === "employee_id" ? EMPLOYEE_ID_HEADER : (FIELD_LABELS[key] ?? key);

// Runs the same field rules as the edit drawer over every row. A row that
// fails them is marked as an issue, unless the employee check already flagged it.
export const applyValidation = (rows) =>
  rows.map((row) => {
    const identifierMessages = Object.entries(row[IDENTIFIER_WARNINGS_KEY] ?? {}).map(
      ([key, message]) => `${fieldLabel(key)}: ${message}`,
    );
    const messages = Object.entries(validateEditValues(rowToFormValues(row))).map(
      // "Employee Id: Employee Id is required" repeats itself, so that one
      // field's message is shown as-is instead of prefixed with its label.
      ([key, message]) => (key === "employee_id" ? message : `${fieldLabel(key)}: ${message}`),
    );
    const allMessages = [...identifierMessages, ...messages];
    const keepStatus = allMessages.length === 0 || isIssueStatus(row.check_status);
    return {
      ...row,
      check_status: keepStatus ? row.check_status : `Invalid: ${allMessages[0]}`,
      validation_errors: allMessages,
    };
  });

const DUPLICATE_ID_STATUS = "Duplicate Employee ID in upload file";

// Two rows in the same upload sharing an Employee Id would otherwise both
// show "valid" and silently overwrite each other on the backend, so every
// row sharing an id is flagged — this always overrides whatever status the
// employee-code check or field validation set.
const flagDuplicateEmployeeIds = (rows) => {
  const counts = {};
  rows.forEach((row) => {
    const key = optionalFormValue(row.employee_id)?.toLowerCase();
    if (!key) return;
    counts[key] = (counts[key] ?? 0) + 1;
  });
  return rows.map((row) => {
    const key = optionalFormValue(row.employee_id)?.toLowerCase();
    return key && counts[key] > 1
      ? { ...row, check_status: DUPLICATE_ID_STATUS }
      : row;
  });
};

// Every reason a row needs review, shown in the info tooltip.
const issueDetails = (row) => {
  const details = [...(row.validation_errors ?? [])];
  const status = row.check_status ?? "";
  if (isIssueStatus(status) && !status.startsWith("Invalid:")) {
    details.unshift(status);
  }
  return details;
};

const rowToFormValues = (row) => {
  const values = { employee_id: row.employee_id ?? "" };
  OPTIONAL_PERSONAL_COLUMNS.forEach((column) => {
    values[column.key] = "";
  });
  OPTIONAL_PERSONAL_COLUMNS.forEach((column) => {
    if (column.key.startsWith("emergency_")) return;
    values[column.key] = row[column.key] ?? "";
  });
  const contact = row.emergency_contacts?.[0];
  values.emergency_contact_name = contact?.contact_name ?? "";
  values.emergency_relation_type = contact?.relation_type ?? "";
  values.emergency_contact_number = contact?.contact_number ?? "";
  return values;
};

const SAMPLE_VALUES = {
  [EMPLOYEE_ID_HEADER]: "ORGAN1234567",
  "Date of Birth": "01-01-1990",
  "Aadhar Number": "123456789012",
  "PAN Number": "ABCDE1234F",
  "ESI Number": "",
  "PF Number": "",
  "UAN Number": "",
  Gender: "Male",
  "Marital Status": "Single",
  "Communication Address": "12 Main Street Chennai",
  "Permanent Address": "12 Main Street Chennai",
  "Account Holder Name": "Organflow",
  "Account Number": "1208111114886",
  "Bank Name": "CANARA BANK",
  "Bank Address": "Erode",
  IFSC: "CNRB0001208",
  "Emergency Contact Name": "Organflow Admin",
  "Emergency Relation Type": "Father",
  "Emergency Contact Number": "9876543210",
  "Employee Use": "Company accommodation",
  "Block Name": "Block A",
  "Room No": "101",
};

const SAMPLE_EXCEL_FILE_NAME = "Sample Employee Update.xlsx";
const EXCEL_TEMPLATE_ROW_COUNT = 500;

// Columns long enough that Excel auto-converts them to scientific notation
// (1.23457E+11) the moment the CSV is opened, silently dropping digits.
const EXCEL_TEXT_SAFE_LABELS = new Set([
  EMPLOYEE_ID_HEADER,
  "Aadhar Number",
  "PAN Number",
  "PF Number",
  "ESI Number",
  "UAN Number",
  "Account Number",
  "Emergency Contact Number",
]);

export const createEmployeeUpdateWorkbook = () => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet([
    IMPORT_HEADERS,
    IMPORT_HEADERS.map((label) => SAMPLE_VALUES[label] ?? ""),
  ]);

  IMPORT_HEADERS.forEach((label, columnIndex) => {
    if (!EXCEL_TEXT_SAFE_LABELS.has(label)) return;
    for (let rowIndex = 1; rowIndex <= EXCEL_TEMPLATE_ROW_COUNT; rowIndex += 1) {
      const address = cellAddress(rowIndex, columnIndex);
      worksheet[address] = {
        t: "s",
        v: String(worksheet[address]?.v ?? ""),
        z: "@",
      };
    }
  });
  worksheet["!ref"] = `A1:${cellAddress(EXCEL_TEMPLATE_ROW_COUNT, IMPORT_HEADERS.length - 1)}`;
  worksheet["!cols"] = IMPORT_HEADERS.map((label) => ({
    wch: EXCEL_TEXT_SAFE_LABELS.has(label) ? 22 : Math.max(12, label.length + 2),
    z: EXCEL_TEXT_SAFE_LABELS.has(label) ? "@" : undefined,
  }));
  XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Update");
  return workbook;
};

const isIssueStatus = (status) =>
  /not|invalid|missing|error|duplicate|required/i.test(status ?? "");

// The backend's own status strings aren't always capitalized the same way
// as the "Invalid: ..." messages generated client-side, so this normalizes
// a leading "invalid" to match regardless of which side produced it.
const normalizeStatusCasing = (status) =>
  typeof status === "string" ? status.replace(/^invalid\b/i, "Invalid") : status;

// Long free-text fields wrap; short codes and dates stay on one line so every
// row keeps the same height and the columns line up with their headers.
const WRAPPING_COLUMN_KEYS = [
  "address",
  "permanent_address",
  "bank_address",
  "transport_type",
  "account_holder_name",
  "bankname",
];

const columnSx = (key) => {
  if (key === "employee_id") return { minWidth: 130 };
  if (key === "status") return { minWidth: 120 };
  if (key === "actions") return { minWidth: 80, width: 80 };
  const wraps = WRAPPING_COLUMN_KEYS.includes(key);
  return {
    minWidth: wraps ? 180 : 120,
    maxWidth: wraps ? 240 : undefined,
    whiteSpace: wraps ? "normal" : "nowrap",
  };
};

const headCellSx = {
  color: themes.headLine,
  fontWeight: "bold",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: 180,
  padding: "10px 12px",
};

const bodyCellSx = {
  verticalAlign: "middle",
  lineHeight: 1.4,
  padding: "8px 12px",
};

const FIELD_LABELS = Object.fromEntries(
  OPTIONAL_PERSONAL_COLUMNS.map((column) => [column.key, column.label]),
);

// Matches the digits-only typing restriction on the regular Employee edit
// "Personal Info" tab (EditEmpPersonalInfo.js: updateAadharCode/updateESINumber).
const NUMERIC_ONLY_FIELD_MAX_LENGTH = {
  aadhar: 12,
  esi: 30,
};

// Matches the alphanumeric typing restriction for PAN on that same page
// (updatePanNumber: specialCharacterRestrict + 10-character cap).
const ALPHANUMERIC_FIELD_MAX_LENGTH = {
  pan: 10,
};

const FIELD_HINTS = {
  dob: "Employee's date of birth for age.",
  aadhar: "Employee's 12-digit Aadhar number.",
  pan: "Employee's PAN identification number.",
  esi: "Employee's ESI identification number.",
  pf: "Employee's PF identification number.",
  uan: "Employee's UAN identification number.",
  gender: "Employee's gender as per official records.",
  marital_status: "Employee's current marital status.",
  address: "Primary address used for correspondence and communication.",
  permanent_address: "Employee's long-term residential address.",
  account_holder_name: "Name of the account holder.",
  accountno: "Bank account number.",
  bankname: "Name of the bank.",
  bank_address: "Branch or bank address.",
  ifsc: "IFSC code of the bank branch.",
  emergency_contact_name: "Name of the emergency contact.",
  emergency_relation_type: "Relation with the employee.",
  emergency_contact_number: "Phone number of the emergency contact.",
  transport_type: "Select the type of accommodation or transport facility assigned to the employee.",
  block_name: "Name of the building / block.",
  room_no: "Room number assigned to the employee.",
};

// The form stores dates as dd-mm-yyyy (as in the CSV), while the date picker needs yyyy-mm-dd.
const toPickerDate = (value) => {
  if (!value) return "";
  const parts = value.split("-");
  return parts.length === 3 && parts[0].length === 2 ? parts.reverse().join("-") : value;
};

const fromPickerDate = (value) => {
  if (!value) return "";
  const parts = value.split("-");
  return parts.length === 3 && parts[0].length === 4 ? parts.reverse().join("-") : value;
};

const EDIT_SECTIONS = [
  {
    title: "Identity & Statutory",
    keys: ["dob", "aadhar", "pan", "esi", "pf", "uan"],
  },
  {
    title: "Address Info",
    keys: ["gender", "marital_status", "address", "permanent_address"],
    wide: ["address", "permanent_address"],
  },
  {
    title: "Bank Info",
    keys: ["account_holder_name", "accountno", "bankname", "bank_address", "ifsc"],
  },
  {
    title: "Emergency Contact",
    keys: [
      "emergency_contact_name",
      "emergency_relation_type",
      "emergency_contact_number",
    ],
  },
  {
    title: "Accommodation & Transport",
    keys: ["transport_type", "block_name", "room_no"],
  },
];

const cellValue = (row, column) => {
  if (column.key === "emergency_contact_name")
    return row.emergency_contacts?.[0]?.contact_name ?? "N/A";
  if (column.key === "emergency_relation_type")
    return row.emergency_contacts?.[0]?.relation_type ?? "N/A";
  if (column.key === "emergency_contact_number")
    return row.emergency_contacts?.[0]?.contact_number ?? "N/A";
  return row[column.key] ?? "N/A";
};

export const BulkEmployeeUpdatePage = () => {
  const history = useHistory();
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [updateRows, setUpdateRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState("");
  const [showListView, setShowListView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [editRow, setEditRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const { result: genderChoices } = useFetch(`${domain}get-gender-choices/`);
  const { result: maritalChoices } = useFetch(`${domain}get-marital-choices/`);

  const choiceLabels = (choices) =>
    (Array.isArray(choices) ? choices : []).map((choice) =>
      typeof choice === "string" ? choice : choice?.label,
    );

  const renderEditField = (key) => {
    const label = FIELD_LABELS[key];
    const hint = FIELD_HINTS[key];
    const fieldValue = editValues[key] ?? "";
    const error = formErrors[key];
    const errorProps = { error: Boolean(error), helperText: error || "" };
    const update = (value) => {
      setEditValues((prev) => ({ ...prev, [key]: value }));
      setFormErrors((prev) => ({ ...prev, [key]: undefined }));
    };

    const selectOptions =
      key === "gender"
        ? choiceLabels(genderChoices)
        : key === "marital_status"
          ? choiceLabels(maritalChoices)
          : key === "transport_type"
            ? employee_use.map((option) => option.label)
            : null;

    return (
      <React.Fragment>
        <Mui.Typography sx={{ fontWeight: 600 }}>{label}</Mui.Typography>
        {hint && <Mui.Typography {...infoStyle}>{hint}</Mui.Typography>}
        {key === "dob" ? (
          <Mui.TextField
            {...errorProps}
            fullWidth
            size="small"
            type="date"
            value={toPickerDate(fieldValue)}
            onChange={(event) => update(fromPickerDate(event.target.value))}
          />
        ) : selectOptions ? (
          <Mui.TextField
            {...errorProps}
            select
            fullWidth
            size="small"
            value={fieldValue}
            onChange={(event) => update(event.target.value)}
          >
            {selectOptions.map((option) => (
              <Mui.MenuItem key={option} value={option}>
                {option}
              </Mui.MenuItem>
            ))}
          </Mui.TextField>
        ) : key === "address" || key === "permanent_address" ? (
          <Mui.TextField
            {...errorProps}
            fullWidth
            multiline
            minRows={3}
            value={fieldValue}
            onChange={(event) => update(event.target.value)}
          />
        ) : NUMERIC_ONLY_FIELD_MAX_LENGTH[key] ? (
          <Mui.TextField
            {...errorProps}
            fullWidth
            size="small"
            value={fieldValue}
            onChange={(event) => {
              const { value } = event.target;
              if (
                /^\d*$/.test(value) &&
                value.length <= NUMERIC_ONLY_FIELD_MAX_LENGTH[key]
              ) {
                update(value);
              }
            }}
          />
        ) : ALPHANUMERIC_FIELD_MAX_LENGTH[key] ? (
          <Mui.TextField
            {...errorProps}
            fullWidth
            size="small"
            value={fieldValue}
            onChange={(event) => {
              const { value } = event.target;
              if (
                specialCharacterRestrict.test(value) &&
                value.length <= ALPHANUMERIC_FIELD_MAX_LENGTH[key]
              ) {
                update(value);
              }
            }}
          />
        ) : (
          <Mui.TextField
            {...errorProps}
            fullWidth
            size="small"
            value={fieldValue}
            onChange={(event) => update(event.target.value)}
          />
        )}
      </React.Fragment>
    );
  };

  const header = [
    ...IMPORT_HEADERS,
    "Status",
    "Actions",
  ];

  const downloadSampleFile = () => {
    XLSX.writeFile(createEmployeeUpdateWorkbook(), SAMPLE_EXCEL_FILE_NAME);
  };

  const checkEmployeeCodes = async (rows) => {
    const unchecked = rows.map((row) => ({ ...row, check_status: "Not checked" }));
    // A blank Employee Id has nothing to look up, and sending it to the
    // backend would come back as a false "not found" — it's flagged here
    // directly instead, and left out of the request.
    const rowsWithId = rows.filter((row) => optionalFormValue(row.employee_id));
    if (rowsWithId.length === 0) {
      return rows.map((row) => ({ ...row, check_status: "Employee ID is required" }));
    }
    try {
      const response = await fetch(`${domain}hrzone/bulk-employee/check-codes/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify(rowsWithId.map(stripInternalFields)),
      });
      if (!response.ok) {
        showToast("error", "Couldn't check employee ids. Please try again.");
        return unchecked;
      }
      const result = await response.json();
      const items = Array.isArray(result) ? result : (result?.results ?? []);
      const itemById = Object.fromEntries(
        items.map((item) => [String(item.employee_id), item]),
      );
      return rows.map((row) => {
        if (!optionalFormValue(row.employee_id)) {
          return { ...row, check_status: "Employee ID is required" };
        }
        const item = itemById[String(row.employee_id)];
        if (!item) return { ...row, check_status: `Employee ID '${row.employee_id}' not found` };
        const { employee_id, status, ...values } = item;
        const normalizedStatus = normalizeStatusCasing(status);
        // The backend sometimes returns the bare word "Invalid" with no
        // further detail for an id it doesn't recognize — swap it for a
        // message that actually says what's wrong.
        const resolvedStatus =
          normalizedStatus?.trim().toLowerCase() === "invalid"
            ? `Employee ID '${row.employee_id}' does not exist`
            : (normalizedStatus ?? "Found");
        return {
          ...mergeCheckedEmployeeValues(row, values),
          rowid: row.rowid,
          employee_id: row.employee_id,
          check_status: resolvedStatus,
        };
      });
    } catch (error) {
      console.error("[BulkEmployeeUpdate] check-codes failed:", error);
      showToast("error", "Couldn't check employee ids. Please try again.");
      return unchecked;
    }
  };

  // Employee-code check, field validation, then duplicate-id flagging — the
  // full pipeline used at upload and again right before submit.
  const checkValidateAndFlag = async (rows) => {
    const checkedRows = await checkEmployeeCodes(rows);
    const validatedRows = applyValidation(checkedRows);
    return flagDuplicateEmployeeIds(validatedRows);
  };

  const handleParsedRows = async (importRows) => {
    const rows = buildUpdateRows(importRows);
    if (rows.length === 0) {
      setLoading(false);
      showToast("error", "No employee rows found. Employee Id is required.");
      return;
    }
    const checkedRows = await checkValidateAndFlag(rows);
    const issueTotal = checkedRows.filter((row) => isIssueStatus(row.check_status)).length;
    setLoading(false);
    if (issueTotal > 0) {
      showToast("error", `${issueTotal} row(s) need review. Check the Status column.`);
    }
    setUpdateRows(checkedRows);
    setPage(0);
    setShowListView(true);
  };

  const isCsvFile = (file) =>
    file.name.toLowerCase().endsWith(".csv") || file.type === "text/csv";

  const isExcelFile = (file) => /\.(xlsx|xls)$/i.test(file.name);

  const parseExcelFile = async (file) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array", raw: true });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    return worksheetToImportRows(worksheet);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    e.target.value = null;
    if (!file) return;
    if (!isCsvFile(file) && !isExcelFile(file)) {
      showToast("error", "Only .xlsx, .xls, and .csv files are supported");
      return;
    }
    setFileName(file.name);
    setFileSize((file.size / 1024).toFixed(2) + " KB");
    setLoading(true);
    if (isExcelFile(file)) {
      parseExcelFile(file)
        .then(handleParsedRows)
        .catch((error) => {
          console.error("[BulkEmployeeUpdate] excel parse failed:", error);
          setLoading(false);
          showToast("error", "Couldn't read the Excel file. Please check the template.");
        });
      return;
    }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      complete: async (results) => handleParsedRows(results.data),
      error: (error) => {
        console.error("[BulkEmployeeUpdate] csv parse failed:", error);
        setLoading(false);
        showToast("error", "Couldn't read the CSV file. Please check the file format.");
      },
    });
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditValues(rowToFormValues(row));
    setFormErrors({});
  };

  const removeRow = () => {
    // Re-flag duplicates over what's left — removing one of two rows
    // sharing an Employee Id should clear the other row's duplicate flag.
    const remainingRows = updateRows.filter(
      (row) => row.rowid !== deleteRow.rowid,
    );
    setUpdateRows(flagDuplicateEmployeeIds(remainingRows));
    if (page * rowsPerPage >= remainingRows.length) {
      setPage(0);
    }
    setDeleteRow(null);
  };

  const saveEdit = async () => {
    const errors = validateEditValues(editValues);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      showToast("error", "Please correct the highlighted fields.");
      return;
    }
    const updated = {
      ...buildPayloadRow(editRow.rowid, editValues),
      check_status: editRow.check_status,
    };
    setSavingEdit(true);
    const [checkedSingle] = await checkEmployeeCodes([updated]);
    const [validatedSingle] = applyValidation([checkedSingle]);
    // Re-flag duplicates across every row, not just the one just edited —
    // the edited Employee Id may now collide with (or stop colliding with)
    // another row already in the table.
    const flaggedRows = flagDuplicateEmployeeIds(
      updateRows.map((row) =>
        row.rowid === editRow.rowid ? { ...validatedSingle, rowid: row.rowid } : row,
      ),
    );
    setSavingEdit(false);
    setUpdateRows(flaggedRows);
    setEditRow(null);
    const checked = flaggedRows.find((row) => row.rowid === editRow.rowid);
    if (isIssueStatus(checked?.check_status)) {
      showToast("error", `Saved with issue: ${checked.check_status}`);
    } else {
      showToast("success", "Row updated.");
    }
  };

  const submitUpdate = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const checkedRows = await checkValidateAndFlag(updateRows);
      setUpdateRows(checkedRows);
      if (checkedRows.some((row) => isIssueStatus(row.check_status))) {
        showToast("error", "Some rows have issues. Please review the Status column.");
        return;
      }
      const response = await fetch(`${domain}bulk-employees/update/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify(checkedRows.map(stripInternalFields)),
      });
      if (response.ok) {
        setSummary({ updated: checkedRows.length, skipped: 0, failed: 0 });
      } else {
        setSummary({ updated: 0, skipped: 0, failed: checkedRows.length });
      }
    } catch (error) {
      console.error("[BulkEmployeeUpdate] operation failed:", error);
      showToast("error", "Couldn't update employees. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const clearData = () => {
    setUpdateRows([]);
    setFileName("");
    setFileSize("");
    setShowListView(false);
  };

  const visibleRows = updateRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const goToEmployees = () => history.push("/admin/hrzone/employees");

  const issueCount = updateRows.filter((row) => isIssueStatus(row.check_status)).length;
  const readyCount = updateRows.length - issueCount;

  const pageHeader = (
    <Mui.Box sx={{ width: "100%", px: 3 }}>
      <GlobalBreadcrumb
        sx={{ mb: 0.9 }}
        trail={[
          "People",
          { label: "Employee", onClick: goToEmployees },
          "Employee Update",
        ]}
      />
      <Mui.Typography sx={designType.pageTitle}>Employee Update</Mui.Typography>
      <Mui.Typography sx={{ ...designType.pageSubtitle, maxWidth: 820 }}>
        Matches existing employees by Employee Id and updates the optional
        personal information columns. Blank cells will keep existing values —
        only the values you provide are updated.
      </Mui.Typography>
    </Mui.Box>
  );

  return (
    <React.Fragment>
      {loading ? (
        <Loader info="Loading..." />
      ) : !showListView ? (
        <Mui.Grid container sx={{ paddingTop: 3 }}>
          <Mui.Grid xs={12}>{pageHeader}</Mui.Grid>
          <Mui.Grid
            xs={12}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "calc(100vh - 280px)",
              paddingTop: 3,
              paddingBottom: 3,
            }}
          >
            <Mui.Stack spacing={3} sx={{ px: 3, width: "100%", maxWidth: 900 }}>
              <Mui.Box
                component="label"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  py: 5,
                  px: 3,
                  cursor: "pointer",
                  textAlign: "center",
                  border: `1.5px dashed ${themes.primaryIndicator}`,
                  borderRadius: 2,
                  backgroundColor: themes.primary,
                  transition: "background-color 0.2s",
                  "&:hover": { backgroundColor: "#F5FBF8" },
                }}
              >
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv,text/csv"
                  hidden
                  onChange={handleFileUpload}
                />
                <Mui.Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#E7FAF5",
                    color: themes.primaryIndicator,
                  }}
                >
                  <CloudDownload fontSize="large" />
                </Mui.Box>
                <Mui.Typography sx={{ fontSize: 17, fontWeight: 600, color: themes.headLine }}>
                  Drop your Excel or CSV file here
                </Mui.Typography>
                <Mui.Typography sx={{ fontSize: 13, color: themes.colorShadow }}>
                  Supports .xlsx, .xls, and .csv. Employee Id is required in every row.
                </Mui.Typography>
                <Mui.Button
                  component="span"
                  variant="contained"
                  sx={{
                    mt: 1,
                    textTransform: "capitalize",
                    backgroundColor: themes.primaryIndicator,
                    color: themes.headLine,
                    "&:hover": { backgroundColor: themes.primaryIndicator, opacity: 0.9 },
                  }}
                >
                  Choose file
                </Mui.Button>
              </Mui.Box>

              <Mui.Stack spacing={1}>
                <Mui.Typography sx={{ fontSize: 13, fontWeight: 600, color: themes.colorShadow, textTransform: "uppercase", letterSpacing: 0.6 }}>
                  Need a template?
                </Mui.Typography>
                <Mui.Paper
                  variant="outlined"
                  sx={{ display: "flex", alignItems: "center", gap: 2, px: 2, py: 1.5 }}
                >
                  <Description sx={{ color: themes.primaryIndicator, fontSize: 36 }} />
                  <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
                    <Mui.Typography sx={{ fontWeight: 600, color: themes.headLine }}>
                      Sample Employee Update.xlsx
                    </Mui.Typography>
                    <Mui.Typography sx={{ fontSize: 13, color: themes.colorShadow }}>
                      Includes text-formatted identifier columns to protect Aadhaar and other IDs.
                    </Mui.Typography>
                  </Mui.Box>
                  <Mui.Tooltip title="Download sample">
                    <Mui.IconButton onClick={downloadSampleFile}>
                      <DownloadForOffline sx={{ color: themes.downloadIconColor, fontSize: 40 }} />
                    </Mui.IconButton>
                  </Mui.Tooltip>
                </Mui.Paper>
              </Mui.Stack>
            </Mui.Stack>
          </Mui.Grid>
        </Mui.Grid>
      ) : (
        <Mui.Grid container sx={{ paddingTop: 3 }}>
          <Mui.Grid xs={12} sx={{ paddingBottom: 2 }}>
            {pageHeader}
          </Mui.Grid>
          <Mui.Paper
            variant="outlined"
            sx={{
              width: "100%",
              mb: 2,
              px: 2,
              py: 1.5,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Description sx={{ color: themes.primaryIndicator, fontSize: 36 }} />
              <Mui.Box sx={{ minWidth: 0 }}>
                <Mui.Typography
                  sx={{ fontWeight: 600, color: themes.headLine, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  title={fileName}
                >
                  {fileName}
                </Mui.Typography>
                <Mui.Typography sx={{ fontSize: 13, color: themes.colorShadow }}>
                  {fileSize} · {updateRows.length} row{updateRows.length === 1 ? "" : "s"}
                </Mui.Typography>
              </Mui.Box>
              <Mui.Tooltip title="Remove file">
                <Mui.IconButton onClick={clearData} size="small">
                  <Cancel sx={{ color: themes.headLine }} />
                </Mui.IconButton>
              </Mui.Tooltip>
            </Mui.Stack>
            <Mui.Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Mui.Chip size="small" label={`Ready: ${readyCount}`} sx={{ color: themes.greenColor, fontWeight: 600 }} variant="outlined" />
              <Mui.Chip size="small" label={`Needs review: ${issueCount}`} sx={{ color: themes.redColor, fontWeight: 600 }} variant="outlined" />
              <Mui.Button
                variant="contained"
                disabled={submitting || readyCount === 0 || issueCount > 0}
                onClick={() => setConfirmOpen(true)}
                sx={{
                  minWidth: 170,
                  textTransform: "capitalize",
                  backgroundColor: themes.primaryIndicator,
                  color: themes.headLine,
                  "&:hover": { backgroundColor: themes.primaryIndicator, opacity: 0.9 },
                  "&.Mui-disabled": { backgroundColor: themes.shadowColor },
                }}
              >
                {submitting ? "Updating..." : "Update Employees"}
              </Mui.Button>
            </Mui.Stack>
          </Mui.Paper>

          <Mui.TableContainer component={Mui.Paper} sx={{ width: "100%", maxHeight: 840, paddingRight: 1 }}>
            <Mui.Table>
              <Mui.TableHead
                sx={{ backgroundColor: themes.primary, position: "sticky", top: -1, zIndex: 10 }}
              >
                <Mui.TableRow>
                  <Mui.TableCell
                    title={EMPLOYEE_ID_HEADER}
                    sx={{ ...columnSx("employee_id"), ...headCellSx }}
                  >
                    {EMPLOYEE_ID_HEADER}
                  </Mui.TableCell>
                  {OPTIONAL_PERSONAL_COLUMNS.map((column) => (
                    <Mui.TableCell
                      key={column.key}
                      title={column.label}
                      sx={{ ...columnSx(column.key), ...headCellSx }}
                    >
                      {column.label}
                    </Mui.TableCell>
                  ))}
                  <Mui.TableCell title="Status" sx={{ ...columnSx("status"), ...headCellSx }}>
                    Status
                  </Mui.TableCell>
                  <Mui.TableCell
                    title="Actions"
                    sx={{ ...columnSx("actions"), ...headCellSx }}
                    align="center"
                  >
                    Actions
                  </Mui.TableCell>
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {updateRows.length === 0 ? (
                  <Mui.TableRow>
                    <Mui.TableCell colSpan={header.length} align="center">
                      <Mui.Alert severity="info">No data available</Mui.Alert>
                    </Mui.TableCell>
                  </Mui.TableRow>
                ) : (
                  visibleRows.map((row) => {
                    const status = row.check_status ?? "Not checked";
                    const hasIssue = isIssueStatus(status);
                    return (
                      <Mui.TableRow key={row.rowid} hover>
                        <Mui.TableCell sx={{ ...bodyCellSx, ...columnSx("employee_id") }}>
                          {row.employee_id}
                        </Mui.TableCell>
                        {OPTIONAL_PERSONAL_COLUMNS.map((column) => (
                          <Mui.TableCell
                            key={column.key}
                            sx={{ ...bodyCellSx, ...columnSx(column.key) }}
                          >
                            {cellValue(row, column)}
                          </Mui.TableCell>
                        ))}
                        <Mui.TableCell
                          sx={{
                            ...bodyCellSx,
                            ...columnSx("status"),
                            color: hasIssue ? themes.redColor : themes.greenColor,
                            fontWeight: "bold",
                          }}
                        >
                          {hasIssue ? "Invalid" : "Valid"}
                        </Mui.TableCell>
                        <Mui.TableCell sx={{ ...bodyCellSx, ...columnSx("actions") }} align="center">
                          <Mui.Stack direction="row" alignItems="center" justifyContent="center">
                            {hasIssue && (
                              <Mui.Tooltip
                                arrow
                                placement="left"
                                title={
                                  <Mui.Box sx={{ py: 0.5 }}>
                                    {issueDetails(row).map((detail) => (
                                      <Mui.Typography key={detail} sx={{ fontSize: 13 }}>
                                        • {detail}
                                      </Mui.Typography>
                                    ))}
                                  </Mui.Box>
                                }
                              >
                                <Mui.IconButton size="small" aria-label="Issue details">
                                  <InfoOutlined sx={{ color: themes.redColor }} fontSize="small" />
                                </Mui.IconButton>
                              </Mui.Tooltip>
                            )}
                            <Mui.IconButton onClick={() => openEdit(row)}>
                              <Edit />
                            </Mui.IconButton>
                            <Mui.IconButton
                              onClick={() => setDeleteRow(row)}
                              aria-label="Remove row"
                            >
                              <Delete sx={{ color: themes.redColor }} />
                            </Mui.IconButton>
                          </Mui.Stack>
                        </Mui.TableCell>
                      </Mui.TableRow>
                    );
                  })
                )}
              </Mui.TableBody>
            </Mui.Table>
            {updateRows.length > 10 && (
              <Mui.TablePagination
                className="custom-pagination"
                component="div"
                rowsPerPageOptions={[10, 20, 50, 100]}
                count={updateRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
            )}
          </Mui.TableContainer>
        </Mui.Grid>
      )}

      <Mui.Drawer
        anchor="right"
        open={Boolean(editRow)}
        onClose={() => setEditRow(null)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "680px",
            maxWidth: "90vw",
            backgroundColor: themes.primary,
          },
        }}
      >
        <Mui.Box sx={{ p: 2 }}>
          <Mui.Box
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}
          >
            <Mui.Typography variant="h6" sx={{ fontWeight: 700 }}>
              Edit Employee Details
            </Mui.Typography>
            <Mui.IconButton onClick={() => setEditRow(null)} aria-label="Close">
              <Close />
            </Mui.IconButton>
          </Mui.Box>

          {editRow && issueDetails(editRow).length > 0 && (
            <Mui.Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                px: 2,
                py: 1.25,
                mb: 2,
                borderRadius: 1.5,
                backgroundColor: themes.whiteColor,
                border: "1px solid rgba(239, 68, 68, 0.3)",
              }}
            >
              <ErrorOutlineRounded sx={{ color: "#b91c1c", fontSize: 20, mt: 0.25 }} />
              <Mui.Box>
                {issueDetails(editRow).map((detail, index) => (
                  <Mui.Typography
                    key={index}
                    variant="body2"
                    sx={{
                      color: "#b91c1c",
                      fontWeight: 700,
                      lineHeight: 1.4,
                    }}
                  >
                    {detail}
                  </Mui.Typography>
                ))}
              </Mui.Box>
            </Mui.Box>
          )}

          <Mui.Paper elevation={1} sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
            <Mui.Typography sx={{ fontWeight: 700, color: themes.headLine, mb: 2 }}>
              Basic Information
            </Mui.Typography>
            <Mui.Typography sx={{ fontWeight: 600 }}>{EMPLOYEE_ID_HEADER}</Mui.Typography>
            <Mui.TextField
              fullWidth
              size="small"
              error={Boolean(formErrors.employee_id)}
              helperText={formErrors.employee_id || ""}
              value={editValues.employee_id ?? ""}
              onChange={(event) => {
                setEditValues((prev) => ({ ...prev, employee_id: event.target.value }));
                setFormErrors((prev) => ({ ...prev, employee_id: undefined }));
              }}
            />
          </Mui.Paper>

          {EDIT_SECTIONS.map((section) => (
            <Mui.Paper key={section.title} elevation={1} sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
              <Mui.Typography sx={{ fontWeight: 700, color: themes.headLine, mb: 2 }}>
                {section.title}
              </Mui.Typography>
              <Mui.Grid container spacing={2}>
                {section.keys.map((key) => (
                  <Mui.Grid item xs={section.wide?.includes(key) ? 12 : 6} key={key}>
                    {renderEditField(key)}
                  </Mui.Grid>
                ))}
              </Mui.Grid>
            </Mui.Paper>
          ))}

          <Mui.Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
            <Mui.Button onClick={() => setEditRow(null)}>Cancel</Mui.Button>
            <Mui.Button variant="contained" onClick={saveEdit} disabled={savingEdit}>
              Save
            </Mui.Button>
          </Mui.Box>
        </Mui.Box>
      </Mui.Drawer>

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={submitUpdate}
        title="Update Employees"
        message={`Are you sure you want to update ${readyCount} employee${
          readyCount === 1 ? "" : "s"
        }?`}
        confirmText="Confirm"
        cancelText="Cancel"
      />

      <ConfirmationDialog
        open={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        onConfirm={removeRow}
        title="Remove Row"
        message={`Do you want to remove the row for Employee Id "${
          deleteRow?.employee_id || "N/A"
        }"?`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <Mui.Dialog
        open={Boolean(summary)}
        onClose={() => setSummary(null)}
        aria-labelledby="update-summary-title"
        maxWidth="sm"
        fullWidth
      >
        <Mui.DialogTitle id="update-summary-title" sx={designType.panelTitle}>
          Update Summary
        </Mui.DialogTitle>
        <Mui.DialogContent>
          <Mui.DialogContentText sx={{ color: themes.greenColor, fontWeight: 600 }}>
            Updated: {summary?.updated ?? 0}
          </Mui.DialogContentText>
          <Mui.DialogContentText>Skipped: {summary?.skipped ?? 0}</Mui.DialogContentText>
          <Mui.DialogContentText sx={{ color: themes.redColor, fontWeight: 600 }}>
            Failed: {summary?.failed ?? 0}
          </Mui.DialogContentText>
        </Mui.DialogContent>
        <Mui.DialogActions sx={{ px: 3, pb: 3 }}>
          <PrimaryButton
            onClick={() => {
              const updatedCount = summary?.updated ?? 0;
              setSummary(null);
              if (updatedCount > 0) {
                goToEmployees();
              }
            }}
          >
            Done
          </PrimaryButton>
        </Mui.DialogActions>
      </Mui.Dialog>
    </React.Fragment>
  );
};
